import { toast } from "react-toastify";
import styles from "./OrderDetails.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "../../config";
import axios from "axios";
import { useState, useEffect, useMemo, useRef } from "react";
import Spin from "../Spinner/Spinner";
import {
  FiUser,
  FiShoppingBag,
  FiCreditCard,
  FiCalendar,
  FiDownload,
} from "react-icons/fi";
import { useImageUpload } from "../ClientLogos/hooks/useImageUpload";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatPrice = (amount) => {
  if (amount == null) return "-";
  const rounded = Math.round(amount);
  return `₹${rounded.toLocaleString("en-IN")}/-`;
};

const statusColors = {
  fulfilled: "#03ce00",
  cancelled: "#e12222",
  processing: "#ffa500",
  "active-cart": "#6687fa",
};

export const OrderDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { uploadImage } = useImageUpload();
  const [showOrderImagesDialog, setShowOrderImagesDialog] = useState(false);
  const [isUploadingOrderImage, setIsUploadingOrderImage] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusRef = useRef(null);
  const [downloadingAllImages, setDownloadingAllImages] = useState(false);

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API}/Admin/cartOrders/${id}`, {
        status,
      });
      toast.success("Order status updated");
      await getOrder();
    } catch (error) {
      console.log(error);
      toast.error("Failed to update order status");
    }
  };

  const getOrder = async () => {
    try {
      const response = await axios.get(`${API}/Admin/cartOrders/${id}`);
      setOrder(response.data.cartOrder);
      setLoading(false);
    } catch (error) {
      console.log(error);
      toast.error("Failed to get order");
    }
  };

  useEffect(() => {
    getOrder();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showStatusDropdown]);

  const handleDownloadAllImages = async (productId = "") => {
    setDownloadingAllImages(true);
    try {
      const products = order?.cart?.products || [];

      let urls = [];

      if (productId) {
        const product = products.find((p) => p._id === productId);
        if (!product) {
          return;
        }
        urls = [
          ...(product.images?.product || []),
          ...(product.images?.mockups || []),
        ];
      } else {
        urls = products.flatMap((p) => [
          ...(p.images?.product || []),
          ...(p.images?.mockups || []),
        ]);
      }

      const uniqueUrls = Array.from(new Set(urls.filter(Boolean)));
      if (uniqueUrls.length === 0) return;

      const zip = new JSZip();

      for (const url of uniqueUrls) {
        try {
          const resp = await fetch(url, { mode: "cors" });
          const blob = await resp.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const contentType = blob.type || "";
          const rawName = url.split("/").pop() || "image";

          let extension = "";
          if (contentType.includes("png")) extension = ".png";
          else if (contentType.includes("jpeg") || contentType.includes("jpg"))
            extension = ".jpg";
          else if (contentType.includes("webp")) extension = ".webp";

          const hasExtension = /\.[a-zA-Z0-9]+$/.test(rawName);
          const fileName = hasExtension ? rawName : `${rawName}${extension || ".png"}`;

          zip.file(fileName, arrayBuffer);
        } catch (err) {
          console.error("Failed to fetch image for zip", err);
        }
        setDownloadingAllImages(false);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "order-images.zip");
    } catch (error) {
      console.error("Failed to create zip", error);
    }
  };

  const topCards = useMemo(() => {
    if (loading || !order) return null;

    const amount = order?.cart?.products?.reduce((acc, product) => {
      const discount = order?.cart?.discount || 0;
      const productPrice = product.cost || 0;
      const cost = Number(productPrice) || 0;
      const discountedCost = cost * (1 - discount / 100);
      const gst = Number(product.gst) || 0;
      const quantity = Number(product.quantity) || 0;
      const amount = discountedCost * (1 + gst / 100) * quantity;
      return acc + amount;
    }, 0);
    return [
      {
        label: "CUSTOMER",
        icon: <FiUser className={styles.topCardIcon} />,
        value: order?.user?.name || order?.user?.companyName || "-",
      },
      {
        label: "PRODUCTS",
        icon: <FiShoppingBag className={styles.topCardIcon} />,
        value: order?.cart?.products?.length || 0,
      },
      {
        label: "TOTAL VALUE",
        icon: <FiCreditCard className={styles.topCardIcon} />,
        value: formatPrice(amount || 0),
      },
      {
        label: "ORDER DATE",
        icon: <FiCalendar className={styles.topCardIcon} />,
        value: formatDate(order?.order?.createdAt),
      },
    ];
  }, [order, loading]);

  if (loading) {
    return <Spin />;
  }
  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <div className={styles.header}>
          <h1>
            Order{" "}
            {order?.order?._id
              ? `HM-${order?.order._id.slice(-4).toUpperCase()}`
              : "-"}
          </h1>
          <h4>Placed on {formatDate(order?.order?.createdAt)}</h4>
        </div>
        <div className={styles.statusWrapper} ref={statusRef}>
          <div
            className={styles.statusPill}
            style={{
              backgroundColor:
                statusColors[order?.order?.status] ||
                statusColors["processing"],
            }}
            onClick={() => setShowStatusDropdown((prev) => !prev)}
          >
            {order?.order?.status === "cancelled" ? "Draft" : order?.order?.status}
          </div>
          {showStatusDropdown && (
            <div className={styles.statusDropdown}>
              {Object.keys(statusColors)
                .filter(
                  (s) =>
                    s !== (order?.order?.status || "") && s !== "active-cart",
                )
                .map((status) => (
                  <button
                    key={status}
                    type="button"
                    className={styles.statusOption}
                    onClick={() => {
                      setShowStatusDropdown(false);
                      updateStatus(status);
                    }}
                  >
                    <span
                      className={styles.statusPillSmall}
                      style={{ backgroundColor: statusColors[status] }}
                    >
                      {status === "cancelled" ? "Draft" : status}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
      {topCards && (
        <div className={styles.topCardsContainer}>
          {topCards.map((card) => (
            <TopCard
              key={card.label}
              icon={card.icon}
              label={card.label}
              value={card.value}
            />
          ))}
        </div>
      )}
      <div className={styles.buttonContainer}>
        <button
          onClick={() => handleDownloadAllImages()}
          className={styles.downloadAllImagesBttn}
          disabled={downloadingAllImages}
        >
          {downloadingAllImages ? "Downloading..." : "Download All Images"}
        </button>
        <button
          className={styles.downloadAllImagesBttn}
          type="button"
          onClick={() => setShowOrderImagesDialog(true)}
        >
          View Order Images
        </button>
      </div>
      <div className={styles.productsContainer}>
        <h4>Products in this order</h4>
        <div className={styles.productsList}>
          {order?.cart?.products?.map((product) => (
            <ProductRow
              key={product._id}
              product={product}
              onDownloadImages={handleDownloadAllImages}
            />
          ))}
        </div>
      </div>
      <button
        className={styles.goToCartBttn}
        onClick={() =>
          navigate(`/users/${order?.user?._id}/carts/${order?.cart?._id}`)
        }
      >
        Go to Cart
      </button>
      {showOrderImagesDialog && (
        <OrderImagesDialog
          show={showOrderImagesDialog}
          onClose={() => setShowOrderImagesDialog(false)}
          orderId={id}
          orderImages={
            Array.from(
              new Set([
                ...(order?.order?.orderImages || []),
                ...(order?.cart?.orderImages || []),
              ]),
            ) || []
          }
          uploadImage={uploadImage}
          onUpdated={getOrder}
          isUploading={isUploadingOrderImage}
          setIsUploading={setIsUploadingOrderImage}
        />
      )}
    </div>
  );
};

const TopCard = ({ icon, label, value }) => {
  return (
    <div className={styles.topCard}>
      <div className={styles.topCardIconWrapper}>{icon}</div>
      <div className={styles.topCardContent}>
        <div className={styles.topCardLabel}>{label}</div>
        <div className={styles.topCardValue}>{value}</div>
      </div>
    </div>
  );
};

const OrderImagesDialog = ({
  show,
  onClose,
  orderId,
  orderImages = [],
  uploadImage,
  onUpdated,
  isUploading,
  setIsUploading,
}) => {
  if (!show) return null;

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const uploadedUrl = await uploadImage(file);
      const updatedImages = [...orderImages, uploadedUrl];
      await axios.put(`${API}/Admin/cartOrders/${orderId}`, {
        orderImages: updatedImages,
      });
      toast.success("Order image added");
      onUpdated();
    } catch (error) {
      console.error(error);
      toast.error("Failed to add order image");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleDownloadImage = async (url) => {
    try {
      if (!url) return;

      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      const contentType = blob.type || "";
      const rawName = url.split("/").pop() || "image";

      let extension = "";
      if (contentType.includes("png")) extension = ".png";
      else if (contentType.includes("jpeg") || contentType.includes("jpg"))
        extension = ".jpg";
      else if (contentType.includes("webp")) extension = ".webp";

      const hasExtension = /\.[a-zA-Z0-9]+$/.test(rawName);
      const fileName = hasExtension ? rawName : `${rawName}${extension || ".png"}`;

      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Failed to download image", error);
    }
  };

  return (
    <>
      <div className={styles.orderImagesBackdrop} onClick={onClose} />
      <div className={styles.orderImagesDialog}>
        <div className={styles.orderImagesHeader}>
          <h3 className={styles.orderImagesTitle}>Order Images</h3>
          <button
            type="button"
            className={styles.orderImagesCloseButton}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className={styles.orderImagesContent}>
          {orderImages && orderImages.length > 0 ? (
            <div className={styles.orderImagesList}>
              {orderImages.map((image, idx) => (
                <div
                  key={idx}
                  className={styles.orderImagesItemWrapper}
                >
                  <img
                    src={image}
                    alt="Order"
                    className={styles.orderImagesItem}
                  />
                  <div
                    className={styles.orderImagesItemDownload}
                    onClick={() => handleDownloadImage(image)}
                    aria-label="Download image"
                  >
                    <FiDownload />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.orderImagesEmpty}>No order images yet.</div>
          )}
        </div>

        <div className={styles.orderImagesFooter}>
          <label className={styles.orderImagesUploadButton}>
            {isUploading ? "Uploading image..." : "Add Order Image"}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              disabled={isUploading}
              onChange={handleFileUpload}
            />
          </label>
        </div>
      </div>
    </>
  );
};

const ProductRow = ({ product, onDownloadImages }) => {
  const [open, setOpen] = useState(false);

  const productImages = product?.images?.product || [];
  const mockupImages = product?.images?.mockups || [];

  const handleDownloadImage = async (url) => {
    try {
      if (!url) return;

      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      const contentType = blob.type || "";
      const rawName = url.split("/").pop() || "image";

      let extension = "";
      if (contentType.includes("png")) extension = ".png";
      else if (contentType.includes("jpeg") || contentType.includes("jpg"))
        extension = ".jpg";
      else if (contentType.includes("webp")) extension = ".webp";

      const hasExtension = /\.[a-zA-Z0-9]+$/.test(rawName);
      const fileName = hasExtension ? rawName : `${rawName}${extension || ".png"}`;

      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Failed to download image", error);
    }
  };

  return (
    <div className={styles.productRow}>
      <button
        type="button"
        className={styles.productRowHeader}
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className={styles.productMainInfo}>
          <div className={styles.productTitle}>{product?.name || "-"}</div>
          <div className={styles.productSubInfo}>
            Quantity: {product?.quantity ?? 0}
          </div>
        </div>
        <div className={styles.productHeaderRight}>
          <span className={styles.productImagesCount}>
            {(productImages.length || 0) + (mockupImages.length || 0)} images
          </span>
          <span className={styles.productChevron}>{open ? "▲" : "▼"}</span>
        </div>
      </button>
      <div className={styles.productImagesContainer}>
        {open && (
          <div className={styles.productImagesSection}>
            {onDownloadImages && (
              <div className={styles.productImagesActions}>
                <button
                  type="button"
                  className={styles.productImagesDownloadButton}
                  onClick={() => onDownloadImages(product._id)}
                >
                  <FiDownload style={{ marginRight: 6 }} />
                  Download images
                </button>
              </div>
            )}
            <div className={styles.productImagesRow}>
              <div style={{ flex: 1 }}>
              {productImages.length > 0 && (
                <div className={styles.productImagesGroup}>
                  <div className={styles.productImagesGroupLabel}>
                    Product Images
                  </div>
                  <div className={styles.productImagesGrid}>
                    {productImages.map((img, idx) => (
                      <div key={idx} className={styles.productImageWrapper}>
                        <img
                          src={img}
                          alt={product?.name || "Product"}
                          className={styles.productImageThumb}
                        />
                        <div
                          className={styles.productImageDownload}
                          onClick={() => handleDownloadImage(img)}
                          aria-label="Download image"
                        >
                          <FiDownload />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
              <div style={{ flex: 1 }}>
                {mockupImages.length > 0 && (
                  <div className={styles.productImagesGroup}>
                    <div className={styles.productImagesGroupLabel}>
                      Mockup Images
                    </div>
                    <div className={styles.productImagesGrid}>
                      {mockupImages.map((img, idx) => (
                        <div key={idx} className={styles.productImageWrapper}>
                          <img
                            src={img}
                            alt={product?.name || "Mockup"}
                            className={styles.productImageThumb}
                          />
                          <div
                            className={styles.productImageDownload}
                            onClick={() => handleDownloadImage(img)}
                            aria-label="Download image"
                          >
                            <FiDownload />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
